
import { useState, useEffect, useCallback, useRef } from 'react';
import { TradingViewCrawlerService } from '@/services/TradingViewCrawlerService';

export interface CrawlJobHealth {
  id: string;
  status: string;
  health_status: 'healthy' | 'warning' | 'stale' | 'no_heartbeat';
  seconds_since_heartbeat: number;
  log_count: number;
  last_heartbeat?: string;
  retry_count: number;
  max_retries: number;
}

interface UseHealthOptions {
  runningJobsCount?: number;
  hasRecentActivity?: boolean;
}

export const useCrawlJobsHealth = (options: UseHealthOptions = {}) => {
  const [healthData, setHealthData] = useState<CrawlJobHealth[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isLoadingRef = useRef(false);

  const { runningJobsCount = 0, hasRecentActivity = false } = options;

  const loadHealthData = useCallback(async () => {
    // Prevent concurrent requests
    if (isLoadingRef.current) return;
    
    isLoadingRef.current = true;
    try {
      const data = await TradingViewCrawlerService.getCrawlJobsHealth();
      setHealthData(data);
    } catch (error) {
      console.error('Error loading health data:', error);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  const getRefreshInterval = useCallback(() => {
    // Critical moments - very fast refresh
    if (hasRecentActivity) return 1000; // 1 second
    
    // Active jobs - fast refresh
    if (runningJobsCount > 0) return 2000; // 2 seconds
    
    // Idle state - slower refresh
    return 10000; // 10 seconds
  }, [runningJobsCount, hasRecentActivity]);

  useEffect(() => {
    // Initial load
    loadHealthData();
    
    // Setup interval with smart refresh rate
    const setupInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      const interval = getRefreshInterval();
      console.log(`🔄 Setting health refresh interval to ${interval}ms (running jobs: ${runningJobsCount}, recent activity: ${hasRecentActivity})`);
      
      intervalRef.current = setInterval(loadHealthData, interval);
    };

    setupInterval();
    
    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loadHealthData, getRefreshInterval, runningJobsCount, hasRecentActivity]);

  return { 
    healthData, 
    isLoading, 
    refreshHealth: loadHealthData 
  };
};
